import PhoneNumber from 'awesome-phonenumber';
import {AttachmentableEntity, DocumentBase, ScheduleA, SnoozeNotificationTime} from '.';
import {
  AgentStatus,
  getAgentStatus,
  getSearchTokens,
  GroupRole,
  PayBy,
  PayTo,
  SearchableTokens,
} from '../constants';
import {UniversalTimestamp} from './compat';
import {UserMFASettings} from './Multifactor';
import {ViewBase} from './ViewBase';
import {defaultViewSelector, ViewSelector} from './ViewSelector';

export interface AgentGroup {
  id: string;
  name: string;
  logoUrl: string;
}

export interface AgentOffice {
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface AgentPermissions {
  residuals?: boolean;
}
export interface AdminContact {
  name: string;
  email: string;
}

export type IMerchantSupportModel = {
  customerPhoneNumber?: string | null;
  whiteLabelPhoneNumberHostedByLuqra?: boolean | null;
  riskCommunicationDirectToMerchant?: boolean | null;
  supportCommunicationDirectToMerchant?: boolean | null;
  agentSupportEmail?: string | null;
  underwritingAgentNotificationsEmail?: string | null;
  approvalAgentNotificationsEmail?: string | null;
  ticketAgentNotificationsEmail?: string | null;
  disputesAgentNotificationsEmail?: string | null;
};

export const getDefaultMerchantSupportModel = (
  agentEmail: string,
  phone?: string
): IMerchantSupportModel => ({
  customerPhoneNumber: phone,
  agentSupportEmail: agentEmail,
  underwritingAgentNotificationsEmail: agentEmail,
  approvalAgentNotificationsEmail: agentEmail,
  ticketAgentNotificationsEmail: agentEmail,
  disputesAgentNotificationsEmail: agentEmail,
});

export type SiteJumpAgentInfo = {
  id: string;
  fullName: string;
  groupName: string;
};

export type Agent = DocumentBase &
  SnoozeNotificationTime &
  AttachmentableEntity & {
    id?: string;
    group: AgentGroup;
    role: GroupRole;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    photoUrl?: string;
    dateOfBirth: UniversalTimestamp;
    office: AgentOffice;
    adminContact: AdminContact;
    timeZoneId: string;
    createdAt: UniversalTimestamp;
    isActive: boolean;
    merchantUids?: string[];
    deleted?: boolean | null;
    isDisabled?: boolean | null;
    masterUid?: string; // if agent is master this will be null
    treatAsSubAgent?: boolean; // Used to determine if agent should see the portal as a sub agent. Would be true if agent WAS a sub agent and is now a regular agent
    subAgentUids?: string[]; // this would only include subagents if an agent is a master
    agentCode?: string;
    subAgentCode?: string;
    isGroupOwner?: boolean;
    permissions?: AgentPermissions;
    lastLoginAt?: UniversalTimestamp;
    masterUidSetBy?: string;
    mfa?: UserMFASettings;
    siteJumpAgents?: SiteJumpAgentInfo[];
    routingNumber?: string;
    accountNumber?: string;
    payTo?: PayTo;
    payBy?: PayBy;
    uwNotifyMerchant?: boolean;
    latestScheduleAVersion?: ScheduleA;
    residualOverrideApplicationUids?: string[];
    splitAdditionalServiceFee?: boolean;
    merchantSupportModel?: IMerchantSupportModel;
    isScheduleAVisible?: boolean;
    isVip?: boolean;
    disableOnlineApplications?: boolean;
    enableAgentTicketsLink?: boolean;
    enableAgentResourcesLink?: boolean;
    enableRiskApplicationClassification?: boolean;
    allowToViewSensitiveUserData?: boolean;
    agentResidualShares?: {
      agentId: string;
      agentName: string;
      groupId?: string;
      displayedResiduals: number;
      calculatedResiduals: number;
      effectiveDate?: UniversalTimestamp;
    }[];
    updatedAt?: UniversalTimestamp;
  };

export type AgentRawData = Agent;
export type AgentView = ViewBase<Agent> & {agentStatus: AgentStatus};
export type SearchableAgentView = AgentView & SearchableTokens;

export const selectAgentView: ViewSelector<AgentView, Agent> = snapshot => {
  const data = snapshot.data();

  const agent: AgentView = {
    ...defaultViewSelector(snapshot),
    phone: data.phone ? new PhoneNumber(data.phone, 'US').getNumber('national') : data.phone,
    agentStatus: getAgentStatus(data),
  };

  return agent;
};

export const selectSearchableAgentView: ViewSelector<SearchableAgentView, Agent> = snap => {
  const rawAgent = snap.data();
  const fieldValues = [
    rawAgent.firstName,
    rawAgent.lastName,
    rawAgent.phone,
    rawAgent.email,
    rawAgent.role,
    rawAgent.group.name,
    rawAgent.isActive ? 'active' : 'disabled',
  ];

  if (rawAgent.agentCode) fieldValues.push(rawAgent.agentCode);
  if (rawAgent.subAgentCode) fieldValues.push(rawAgent.subAgentCode);

  const searchTokens = getSearchTokens(fieldValues);
  const agent: SearchableAgentView = {
    ...selectAgentView(snap),
    searchTokens,
  };
  return agent;
};
